import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FiX, FiSearch, FiMessageSquare, FiVideo } from 'react-icons/fi';
import userService from '../../services/userService';
import chatService from '../../services/chatService';
import videoCallService from '../../services/videoCallService';
import { useChat } from '../../context/ChatContext';
import { useVideoCall } from '../../context/VideoCallContext';
import { useDashboard } from '../../context/DashboardContext';
import Avatar from '../Talk/Shared/Avatar';
import { notify } from '../CustomToast';
import './AddressBookPanel.css';

const SKELETON_COUNT = 6;

const UserRowSkeleton = () => (
    <div className="ab-user-row ab-skeleton-row">
        <div className="ab-skeleton ab-skeleton-avatar" />
        <div className="ab-user-info">
            <div className="ab-skeleton ab-skeleton-name" />
            <div className="ab-skeleton ab-skeleton-sub" />
        </div>
        <div className="ab-actions">
            <div className="ab-skeleton ab-skeleton-btn" />
            <div className="ab-skeleton ab-skeleton-btn" />
        </div>
    </div>
);

const AddressBookPanel = ({ isOpen, onClose }) => {
    const [users, setUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [showSearch, setShowSearch] = useState(false);
    const [actionLoading, setActionLoading] = useState(null);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);

    const listRef = useRef(null);
    const searchTimerRef = useRef(null);
    const currentQueryRef = useRef('');

    const { setActiveConversationId, setHideSidebar, fetchConversations } = useChat();
    const { joinMeeting } = useVideoCall();
    const { setActiveView } = useDashboard();

    const loadUsers = useCallback(async (query, pageNum, append = false) => {
        if (append) {
            setIsLoadingMore(true);
        } else {
            setIsLoading(true);
            setError(null);
        }
        try {
            const response = await userService.search(query, pageNum);
            const list = response?.data?.users ?? [];
            const meta = response?.data?.meta ?? {};
            setUsers((prev) => append ? [...prev, ...list] : list);
            setHasMore(meta.has_more ?? false);
            setPage(pageNum);
        } catch (err) {
            if (!append) setError('Failed to load users. Please try again.');
        } finally {
            setIsLoading(false);
            setIsLoadingMore(false);
        }
    }, []);

    // Reset and reload when panel opens
    useEffect(() => {
        if (isOpen) {
            setSearchQuery('');
            setShowSearch(false);
            setUsers([]);
            setPage(1);
            setHasMore(false);
            currentQueryRef.current = '';
            loadUsers('', 1, false);
        }
    }, [isOpen, loadUsers]);

    // Debounced search
    useEffect(() => {
        if (!isOpen) return;
        clearTimeout(searchTimerRef.current);
        searchTimerRef.current = setTimeout(() => {
            currentQueryRef.current = searchQuery;
            setUsers([]);
            setPage(1);
            loadUsers(searchQuery, 1, false);
        }, 300);
        return () => clearTimeout(searchTimerRef.current);
    }, [searchQuery, isOpen, loadUsers]);

    // Escape key
    useEffect(() => {
        if (!isOpen) return;
        const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [isOpen, onClose]);

    // Infinite scroll
    useEffect(() => {
        const list = listRef.current;
        if (!list) return;
        const handleScroll = () => {
            if (isLoadingMore || !hasMore) return;
            if (list.scrollTop + list.clientHeight >= list.scrollHeight - 80) {
                loadUsers(currentQueryRef.current, page + 1, true);
            }
        };
        list.addEventListener('scroll', handleScroll);
        return () => list.removeEventListener('scroll', handleScroll);
    }, [hasMore, isLoadingMore, page, loadUsers]);

    const handleChat = async (user) => {
        if (actionLoading) return;
        setActionLoading(`chat-${user.id}`);
        try {
            const response = await chatService.createConversation({
                type: 'direct',
                user_id: user.id,
            });
            if (response?.status && response?.data?.conversation) {
                const conversation = response.data.conversation;
                await fetchConversations(1);
                setActiveConversationId(conversation.id);
                setHideSidebar(false);
                setActiveView('talk');
                onClose();
            } else {
                notify('error', response?.message || 'Failed to open chat');
            }
        } catch (err) {
            if (err?.status !== 422) {
                notify('error', err?.message || 'Failed to open chat');
            }
        } finally {
            setActionLoading(null);
        }
    };

    const handleCall = async (user) => {
        if (actionLoading) return;
        setActionLoading(`call-${user.id}`);
        try {
            const createResponse = await videoCallService.createInstant({
                invitees: [user.id],
                title: `Call with ${user.name}`,
                meeting_mode: 'one_to_one',
                meeting_type: 'instant',
                call_type: 'voice',
                settings: { recording: false, screen_share: false },
            });
            if (createResponse?.status && createResponse?.data?.meeting) {
                const joinResponse = await videoCallService.join(createResponse.data.meeting.id);
                if (joinResponse?.status) {
                    joinMeeting({
                        ...joinResponse.data.meeting,
                        tokens: joinResponse.data.tokens,
                    });
                    onClose();
                } else {
                    notify('error', joinResponse?.message || 'Failed to join call');
                }
            } else {
                notify('error', createResponse?.message || 'Failed to start call');
            }
        } catch (err) {
            if (err?.status !== 422) {
                notify('error', err?.message || 'Failed to start call');
            }
        } finally {
            setActionLoading(null);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="ab-overlay">
            <div className="ab-drawer" role="dialog" aria-label="Address book directory">
                <div className="ab-header">
                    <span className="ab-title">Address book directory</span>
                    <div className="ab-header-actions">
                        <button
                            className="ab-icon-btn"
                            onClick={() => setShowSearch((v) => !v)}
                            aria-label="Toggle search"
                            type="button"
                        >
                            <FiSearch size={18} />
                        </button>
                        <button
                            className="ab-icon-btn"
                            onClick={onClose}
                            aria-label="Close address book"
                            type="button"
                        >
                            <FiX size={18} />
                        </button>
                    </div>
                </div>

                {showSearch && (
                    <div className="ab-search-bar">
                        <input
                            className="ab-search-input"
                            type="text"
                            placeholder="Search users..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            autoFocus
                        />
                    </div>
                )}

                <div className="ab-list" ref={listRef}>
                    {isLoading && Array.from({ length: SKELETON_COUNT }).map((_, i) => (
                        <UserRowSkeleton key={i} />
                    ))}

                    {!isLoading && error && (
                        <div className="ab-state">
                            <span>{error}</span>
                            <button
                                className="ab-retry-btn"
                                type="button"
                                onClick={() => loadUsers(currentQueryRef.current, 1, false)}
                            >
                                Retry
                            </button>
                        </div>
                    )}

                    {!isLoading && !error && users.length === 0 && (
                        <div className="ab-state">No users found.</div>
                    )}

                    {!isLoading && !error && users.map((user) => (
                        <div key={user.id} className="ab-user-row">
                            <Avatar name={user.name} size={40} />
                            <div className="ab-user-info">
                                <div className="ab-user-name">{user.name}</div>
                                <div className="ab-user-sub">{user.email}</div>
                            </div>
                            <div className="ab-actions">
                                <button
                                    className="ab-action-btn"
                                    type="button"
                                    aria-label={`Chat with ${user.name}`}
                                    disabled={!!actionLoading}
                                    onClick={() => handleChat(user)}
                                >
                                    <FiMessageSquare size={16} />
                                </button>
                                <button
                                    className="ab-action-btn"
                                    type="button"
                                    aria-label={`Call ${user.name}`}
                                    disabled={!!actionLoading}
                                    onClick={() => handleCall(user)}
                                >
                                    <FiVideo size={16} />
                                </button>
                            </div>
                        </div>
                    ))}

                    {isLoadingMore && (
                        <div className="ab-load-more">
                            <UserRowSkeleton />
                            <UserRowSkeleton />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AddressBookPanel;
